import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminAPI } from '../api';
import './exam/Exam.css';

// 색상 맵
const COLOR_MAP: Record<string, string> = {
  '빨강': '#e74c3c', '빨간': '#e74c3c', '파랑': '#3498db', '파란': '#3498db',
  '초록': '#27ae60', '초록색': '#27ae60', '주황': '#e67e22', '보라': '#9b59b6',
  'red': '#e74c3c', 'blue': '#3498db', 'green': '#27ae60',
};

const renderText = (text: string): React.ReactNode => {
  if (!text) return null;
  let cleaned = text.replace(/_x000d_/gi, '').replace(/\r/g, '');
  cleaned = cleaned.replace(/\[([^\]]+)\]([^[]*)\[\/\]/g, (match, color, content) => {
    const code = COLOR_MAP[color.toLowerCase()] || COLOR_MAP[color];
    if (code) return `@@COLOR:${code}@@${content}@@/COLOR@@`;
    return match;
  });
  const brPattern = new RegExp('(\\[\\d+:img:[^\\]]+\\]|\\[\\d+img:[^\\]]+\\]|<br\\s*\\/?>|@@COLOR:[^@]+@@[^@]*@@\\/COLOR@@)', 'gi');
  const parts = cleaned.split(brPattern);
  return (
    <>
      {parts.map((part, i) => {
        const imgMatch = part.match(/^\[(\d+):img:(.+)\]$/) || part.match(/^\[(\d+)img:(.+)\]$/);
        if (imgMatch) {
          const scale = Number(imgMatch[1]);
          const path = imgMatch[2].trim();
          return <img key={i} src={`/images/${path}`} alt="" style={{ width: `${scale}%`, maxWidth: '100%', verticalAlign: 'middle' }} onError={e => (e.currentTarget.style.display = 'none')} />;
        }
        if (/^<br\s*\/?>$/i.test(part)) return <br key={i} />;
        const colorMatch = part.match(/^@@COLOR:([^@]+)@@(.*)@@\/COLOR@@$/);
        if (colorMatch) return <span key={i} style={{ color: colorMatch[1] }}>{colorMatch[2]}</span>;
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

const TrialExam: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const examType = (searchParams.get('type') || '필기') as '필기' | '실기';

  const [questions, setQuestions] = useState<any[]>([]);
  const [curIdx, setCurIdx] = useState(0);
  const [userAns, setUserAns] = useState<Record<number, any>>({});
  const [showAns, setShowAns] = useState<Record<number, boolean>>({});
  const [showExp, setShowExp] = useState<Record<number, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 타이머
  const [remaining, setRemaining] = useState(examType === '필기' ? 9000 : 6000);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    adminAPI.trialQuestions(examType)
      .then(res => {
        setQuestions(res.data.questions);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.error || '문제를 불러올 수 없습니다.');
        setLoading(false);
      });
  }, [examType]);

  useEffect(() => {
    if (questions.length === 0 || submitted) return;
    timerRef.current = setInterval(() => {
      setRemaining(r => {
        if (r <= 1) {
          clearInterval(timerRef.current!);
          setSubmitted(true);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [questions, submitted]);

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60).toString().padStart(2, '0');
    const s = (sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const q = questions[curIdx];
  const totalAnswered = Object.keys(userAns).length;
  const correctCount = submitted
    ? questions.filter(q => String(userAns[q.id]) === String(q.answer)).length : 0;
  const score = submitted ? Math.round(correctCount / questions.length * 100) : 0;
  const isWarning = remaining <= 300;

  const handleSubmit = () => {
    if (totalAnswered < questions.length) {
      if (!window.confirm(`${questions.length - totalAnswered}문제를 풀지 않았습니다. 제출하시겠습니까?`)) return;
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setSubmitted(true);
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: 16, color: '#666' }}>
      문제를 불러오는 중...
    </div>
  );

  if (error) return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 16 }}>
      <div style={{ fontSize: 48 }}>🔒</div>
      <div style={{ fontSize: 18, fontWeight: 700 }}>{error}</div>
      <div style={{ fontSize: 14, color: '#666' }}>곧 서비스될 예정입니다!</div>
      <button className="btn btn-primary" onClick={() => navigate('/')}>메인으로</button>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#f8fafc' }}>

      {/* 상단 topbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', height: 52, background: 'var(--primary)',
        color: 'white', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/')}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>
            ← 나가기
          </button>
          <span style={{ fontWeight: 700, fontSize: 14 }}>
            🎯 {examType} 체험 모의고사
          </span>
          <span style={{ fontSize: 12, opacity: 0.8 }}>
            답안 {totalAnswered}/{questions.length}
          </span>
        </div>

        {/* 타이머 */}
        <div style={{ fontSize: 20, fontWeight: 800, color: isWarning ? '#fca5a5' : 'white' }}>
          ⏱ {formatTime(remaining)}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!submitted && (
            <button className="btn btn-sm"
              style={{ background: '#e74c3c', color: 'white', border: 'none' }}
              onClick={handleSubmit}>
              제출하기
            </button>
          )}
          <button className="btn btn-sm"
            style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none' }}
            onClick={() => navigate('/login')}>
            🔐 로그인
          </button>
        </div>
      </div>

      {/* 결과 오버레이 */}
      {submitted && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ background: 'white', borderRadius: 20, padding: 40, textAlign: 'center', width: 360, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>{score >= 60 ? '🎉' : '📚'}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: score >= 60 ? '#2ecc71' : '#e74c3c', marginBottom: 4 }}>
              {score}점
            </div>
            <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>
              {correctCount}/{questions.length} 정답
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 24, color: score >= 60 ? '#2ecc71' : '#e74c3c' }}>
              {score >= 60 ? '합격!' : '불합격'}
            </div>

            {/* 로그인 유도 */}
            <div style={{ background: '#f0f9ff', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid #bae6fd' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0369a1', marginBottom: 6 }}>
                🚀 더 많은 문제를 풀어보세요!
              </div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 12, lineHeight: 1.6 }}>
                로그인하면 모든 기출문제, 오답노트,<br />
                모의고사 결과 저장까지 무료로 이용 가능합니다.
              </div>
              <button className="btn btn-primary" style={{ width: '100%', fontSize: 14 }}
                onClick={() => navigate('/login')}>
                🔐 로그인 / 회원가입
              </button>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }}
                onClick={() => navigate('/')}>메인으로</button>
              <button className="btn btn-ghost" style={{ flex: 1 }}
                onClick={() => { setSubmitted(false); setCurIdx(0); setUserAns({}); setShowAns({}); setShowExp({}); setRemaining(examType === '필기' ? 9000 : 6000); }}>
                다시풀기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 본문 */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* 문제 목록 사이드바 */}
        <div style={{ width: 180, background: 'white', borderRight: '1px solid var(--border)', overflowY: 'auto', flexShrink: 0 }}>
          <div style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
            문제 목록
          </div>
          {questions.map((mq, i) => {
            const ans = userAns[mq.id] !== undefined;
            const isActive = i === curIdx;
            const correct = submitted && ans && String(userAns[mq.id]) === String(mq.answer);
            const wrong = submitted && ans && String(userAns[mq.id]) !== String(mq.answer);
            return (
              <div key={mq.id} onClick={() => setCurIdx(i)}
                style={{
                  padding: '5px 10px', cursor: 'pointer', fontSize: 11,
                  borderBottom: '1px solid var(--border)',
                  background: isActive ? 'var(--primary)' : correct ? '#d5f5e3' : wrong ? '#fdecea' : ans ? '#fef9c3' : 'white',
                  color: isActive ? 'white' : 'var(--text-secondary)',
                }}>
                {i + 1}번 {ans ? (submitted ? (correct ? '✓' : '✗') : '✓') : ''}
              </div>
            );
          })}
        </div>

        {/* 문제 영역 */}
        <div style={{ flex: 1, overflow: 'auto', padding: 24, background: 'white' }}>
          {q && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, background: '#dbeafe', color: '#2563eb', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
                    {examType}
                  </span>
                  {q.subject_name && (
                    <span style={{ fontSize: 11, background: '#f3e8ff', color: '#7c3aed', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
                      {q.subject_name}
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {q.year}년 {q.round}차 {q.number}번
                  </span>
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)' }}>
                  {curIdx + 1} / {questions.length}
                </span>
              </div>

              {/* 문제 */}
              <div style={{ fontSize: 16, lineHeight: 2, marginBottom: 20, padding: 16, background: '#f8fafc', borderRadius: 10 }}>
                {renderText(q.question)}
              </div>

              {/* 보기 (필기) */}
              {examType === '필기' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                  {[1,2,3,4].map(n => {
                    const optText = q[`option${n}`];
                    if (!optText) return null;
                    const selected = userAns[q.id] === n;
                    const answered = showAns[q.id] || submitted;
                    const correct = Number(n) === Number(q.answer);
                    return (
                      <button key={n}
                        className={`q-option ${selected ? 'selected' : ''} ${answered && correct ? 'correct' : ''} ${answered && selected && !correct ? 'wrong' : ''}`}
                        onClick={() => {
                          if (submitted) return;
                          setUserAns(prev => ({ ...prev, [q.id]: n }));
                        }}>
                        <span className="option-num">{['①','②','③','④'][n-1]}</span>
                        <span>{renderText(optText)}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* 실기 답안 입력 */}
              {examType === '실기' && (
                <textarea className="form-control" rows={4}
                  placeholder="답안을 입력하세요..."
                  value={userAns[q.id] || ''}
                  onChange={e => setUserAns(prev => ({ ...prev, [q.id]: e.target.value }))}
                  style={{ resize: 'vertical', marginBottom: 20 }} />
              )}

              {/* 정답/해설 (필기: 선택 후 or 제출 후) */}
              {(showAns[q.id] || submitted) && (
                <div style={{ background: '#f0fdf4', borderRadius: 10, padding: 16, marginBottom: 16, border: '1px solid #bbf7d0' }}>
                  <div style={{ fontWeight: 700, color: '#16a34a', marginBottom: 8 }}>
                    ✓ 정답: {examType === '필기' ? `${q.answer}번` : renderText(q.answer)}
                    {examType === '필기' && userAns[q.id] !== undefined && (
                      <span style={{ marginLeft: 10, color: String(userAns[q.id]) === String(q.answer) ? '#16a34a' : '#e74c3c', fontSize: 14 }}>
                        {String(userAns[q.id]) === String(q.answer) ? '🎉 정답!' : '❌ 오답'}
                      </span>
                    )}
                  </div>
                  {showExp[q.id] && q.explanation && (
                    <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.8, borderTop: '1px solid #bbf7d0', paddingTop: 8, marginTop: 8 }}>
                      📝 {renderText(q.explanation)}
                    </div>
                  )}
                </div>
              )}

              {/* 버튼 */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-ghost"
                  onClick={() => setCurIdx(i => Math.max(0, i - 1))} disabled={curIdx === 0}>
                  ◀ 이전
                </button>
                {examType === '필기' && (
                  <button className="btn btn-primary"
                    disabled={userAns[q.id] === undefined && !submitted}
                    onClick={() => setShowAns(prev => ({ ...prev, [q.id]: !prev[q.id] }))}>
                    {showAns[q.id] ? '정답 숨기기' : '정답 확인'}
                  </button>
                )}
                {(showAns[q.id] || submitted) && q.explanation && (
                  <button className="btn btn-ghost"
                    onClick={() => setShowExp(prev => ({ ...prev, [q.id]: !prev[q.id] }))}>
                    {showExp[q.id] ? '해설 닫기' : '해설 보기'}
                  </button>
                )}
                <button className="btn btn-ghost"
                  onClick={() => setCurIdx(i => Math.min(questions.length - 1, i + 1))}
                  disabled={curIdx === questions.length - 1}>
                  다음 ▶
                </button>
              </div>

              {/* 로그인 유도 배너 */}
              <div style={{ marginTop: 24, padding: 14, background: '#fef3c7', borderRadius: 10, border: '1px solid #fde68a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 13, color: '#92400e' }}>
                  💡 로그인하면 모든 기출문제와 오답노트를 무료로 이용할 수 있어요!
                </div>
                <button className="btn btn-sm"
                  style={{ background: '#f59e0b', color: 'white', border: 'none', whiteSpace: 'nowrap', marginLeft: 12 }}
                  onClick={() => navigate('/login')}>
                  로그인하기
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrialExam;
